// i18n keys of Admin → Master chart template (COA-1, src/components/Admin/CoaMaster.tsx), en / zh / th.
// ⛔ Fill all three: an empty th value falls back to English (returnEmptyString is off, but '' is still a hole).
// ⛔ Counted keys are _one / _other — `_plural` is dead in i18next 23.
// ⛔ ONLY THE SCREEN'S OWN WORDS ARE HERE. The account names (Thai and English), the Account Type values,
// the Subgroup values, Dr / Cr and the customer's README are the customer's data: the screen shows them
// verbatim in every language, and no Thai or Chinese rendering of them is made up in this file.
export const en: Record<string, string> = {
  coam_tab: 'Chart template',
  coam_title: 'Master chart of accounts template',
  coam_readonly: 'The customer\'s file is read-only: shown exactly as the customer supplied it, and stored separately from every client\'s chart of accounts. The firm\'s own judgements on it are kept beside it, never written into it.',
  coam_template: 'Template',
  coam_loading: 'Loading…',
  coam_none: 'No master chart template is loaded.',
  coam_last_record: 'Last load record:',
  coam_error: 'Could not load the template: {{error}}',
  coam_src_file: 'File',
  coam_src_sheet: 'Sheet',
  coam_src_sha: 'SHA-256',
  coam_src_imported: 'Imported',
  coam_src_by: 'by {{who}}',
  coam_src_by_system: 'with the system itself, from the customer\'s file',
  coam_src_rows: 'Rows',
  coam_count_accounts: 'Accounts',
  coam_count_categories: 'Main categories',
  coam_count_subgroups: 'Subgroups',
  coam_count_types: 'Account types',
  coam_count_depth: 'Tree depth',
  coam_depth_hint: 'The deepest account is {{n}} Parent Account steps below a top-level account.',
  coam_count_more: '{{top}} top-level · Dr {{dr}} / Cr {{cr}} · {{notes}} with notes',
  coam_search_ph: 'Search code, name, Subgroup or Account Type',
  coam_filter_category: 'Main category',
  coam_filter_type: 'Account type',
  coam_all_categories: 'All main categories',
  coam_all_types: 'All account types',
  coam_clear: 'Clear',
  coam_export: 'Download template (.xlsx)',
  coam_export_hint: 'The whole template — all {{n}} accounts and the README, as the customer supplied them — whatever the search shows.',
  coam_col_code: 'Code',
  coam_col_th: 'Thai name',
  coam_col_en: 'English name',
  coam_col_type: 'Account type',
  coam_col_subgroup: 'Subgroup',
  coam_col_side: 'Normal balance',
  coam_col_notes: 'System / notes',
  coam_expand_all: 'Expand all',
  coam_collapse_all: 'Collapse all',
  coam_expand: 'Expand {{code}}',
  coam_collapse: 'Collapse {{code}}',
  coam_showing_all: '{{n}} accounts, arranged by Parent Account. Open a row to see the accounts under it.',
  coam_showing_match: '{{matched}} of {{n}} accounts match. The accounts above them are shown in grey, so the tree can be followed.',
  coam_no_match: 'No account matches.',
  coam_context_tip: 'Shown because an account under it matches',
  coam_opposite_tip: 'Normal balance {{side}}; its top-level account {{top}} is {{topSide}}.',
  coam_legend: 'Marked types',
  coam_kind_header: 'Grouping account, not posted to',
  coam_kind_control: 'Normally controlled by the system (AR, AP, VAT and similar)',
  coam_kind_contra: 'Offsets the account it is paired with, so it carries the opposite side',
  coam_kind_system: 'Generated by the system',
  coam_kind_reference: 'Reference data, not posted to',
  coam_obs_title: 'Observations on the customer\'s file',
  coam_obs_lead: 'This is how the customer\'s file is. The five points below describe the source as supplied — none of them is an error, and nothing on this page changes them.',
  coam_obs1_title: 'Header accounts',
  coam_obs1_all: 'All {{total}} Header accounts have sub-accounts — there is no empty header.',
  coam_obs1_some: '{{with}} of the {{total}} Header accounts have sub-accounts. These have none:',
  coam_obs2_title: 'Other accounts that have sub-accounts',
  coam_obs2_text: 'Accounts that are not Header accounts but have sub-accounts: {{n}}',
  coam_obs2_none: 'Every account that has sub-accounts is a Header account.',
  coam_obs2_note: 'Most likely a control or asset account with detail, accumulated-depreciation or accumulated-amortisation accounts under it. This is the customer\'s design; whether any of them should be read as a Header is a judgement, recorded in the firm\'s layer — never in the customer\'s file.',
  coam_export_hint_firm: 'The customer\'s file exactly as supplied — all {{n}} accounts and the README — without the firm\'s judgements, which are kept apart.',
  coam_col_firm: 'Firm\'s judgement',
  coam_obs2_judged: '{{judged}} of these {{total}} accounts carry a firm judgement on their type.',
  coam_view: 'Show',
  coam_view_customer: 'Customer\'s file',
  coam_view_firm: 'With the firm\'s judgements',
  coam_firm_title: 'The firm\'s judgements',
  coam_firm_ok_one: 'In force — {{count}} judgement',
  coam_firm_ok_other: 'In force — {{count}} judgements',
  coam_firm_blocked: 'Not usable until a person looks',
  coam_firm_lead: 'Rulings the firm has made on the customer\'s file — which accounts are grouping headers, which leaf carries the AR / AP control — each with its reason, who made it and when. Kept beside the customer\'s file, never written into it: the customer\'s cells and the download stay exactly as supplied.',
  coam_firm_blocked_lead: 'The layer is shown, but nothing may copy or publish it while any of this is so:',
  coam_firm_counts: 'Accounts',
  coam_firm_count_header: 'Grouping headers (not posted to)',
  coam_firm_count_postable: 'Posted to',
  coam_firm_count_reference: 'Reference data (not posted to)',
  coam_firm_count_system: 'System generated (not posted to)',
  coam_firm_count_control: 'Typed as a control account',
  coam_firm_count_ar: 'AR control account',
  coam_firm_count_ap: 'AP control account',
  coam_firm_none: '(none)',
  coam_firm_role_ar: 'AR control',
  coam_firm_role_ap: 'AP control',
  coam_firm_field_account_type: 'Account type',
  coam_firm_field_control_role: 'Control role',
  coam_firm_field_name_th: 'Thai name',
  coam_firm_field_name_en: 'English name',
  coam_firm_field_subgroup: 'Subgroup',
  coam_firm_field_system_notes: 'System / notes',
  coam_firm_kept: 'reviewed — the customer\'s value is kept:',
  coam_firm_stale: 'Not in force — the customer\'s cell has changed',
  coam_firm_by: 'judged by {{who}}',
  coam_firm_recorded: 'recorded by {{who}} ({{src}})',
  coam_firm_rule: 'Firm ruling…',
  coam_firm_rule_tip: 'Record the firm\'s judgement on {{code}}',
  coam_firm_dialog_title: 'The firm\'s judgement on {{code}}',
  coam_firm_field: 'Field',
  coam_firm_customer_value: 'Customer\'s value',
  coam_firm_in_force: 'In force now:',
  coam_firm_action: 'Judgement',
  coam_firm_act_set: 'Use a different value',
  coam_firm_act_confirm: 'Reviewed — keep the customer\'s value',
  coam_firm_act_withdraw: 'Withdraw the judgement — back to the customer\'s value',
  coam_firm_value: 'The firm\'s value',
  coam_firm_words_hint: 'Only this template\'s own account types can be chosen.',
  coam_firm_reason: 'Reason',
  coam_firm_decision_ref: 'Decision reference (optional)',
  coam_firm_reason_required: 'A reason is required: say why, in a sentence a reviewer can check.',
  coam_firm_history: 'This cell\'s judgements, newest first',
  coam_firm_cancel: 'Cancel',
  coam_firm_submit: 'Record the judgement',
  coam_firm_discard: 'Close without recording this judgement? What you typed will be lost.',
  coam_sub_count_one: '{{count}} sub-account',
  coam_sub_count_other: '{{count}} sub-accounts',
  coam_obs3_title: 'Normal balance opposite to the category',
  coam_obs3_text: 'Accounts whose normal balance is opposite to their top-level account\'s: {{n}}',
  coam_obs3_none: 'Every account carries the same normal balance as its top-level account.',
  coam_obs3_all_contra: 'All of them are Contra types. A Contra account offsets the account it is paired with, so the opposite side is what it should carry — this is correct, not an error.',
  coam_obs3_some_contra: 'The Contra types among them carry the opposite side by design; the others are listed as the file has them.',
  coam_obs4_title: 'Accounts that share a name',
  coam_obs4_text: 'Same English name: {{en}} · same Thai name: {{th}}. The file names them this way; the codes tell them apart.',
  coam_obs4_none: 'No two accounts share a name.',
  coam_obs4_header_child: 'a Header and its own sub-account',
  coam_obs4_separate: 'two separate accounts',
  coam_obs4_same_both: 'same English and Thai name',
  coam_obs4_same_en: 'same English name',
  coam_obs4_same_th: 'same Thai name',
  coam_obs5_title: 'Special account types',
  coam_obs5_text: 'Account types the file sets apart:',
  coam_obs5_none: 'The file has no Control, System or Reference account type.',
  coam_readme_said: 'The customer\'s README:',
  coam_readme_title: 'The customer\'s README',
  coam_readme_lead: 'Quoted as supplied, not rewritten.',
};
export const zh: Record<string, string> = {
  coam_tab: '科目主模板',
  coam_title: '科目表主模板',
  coam_readonly: '客户原件只读:按客户提供的原样显示,与每个客户自己的科目表分开存放。事务所对它的判断另存在旁边,从不写进原件。',
  coam_template: '模板',
  coam_loading: '加载中…',
  coam_none: '尚未载入科目主模板。',
  coam_last_record: '最近一次载入记录:',
  coam_error: '无法载入模板:{{error}}',
  coam_src_file: '文件',
  coam_src_sheet: '工作表',
  coam_src_sha: 'SHA-256',
  coam_src_imported: '导入时间',
  coam_src_by: '由 {{who}}',
  coam_src_by_system: '随系统一并载入，来自客户的原件',
  coam_src_rows: '行数',
  coam_count_accounts: '科目',
  coam_count_categories: '大类',
  coam_count_subgroups: '子分组',
  coam_count_types: '科目类型',
  coam_count_depth: '树深',
  coam_depth_hint: '最深的科目位于顶层科目之下 {{n}} 层(按 Parent Account 逐级计算)。',
  coam_count_more: '顶层 {{top}} · Dr {{dr}} / Cr {{cr}} · 有备注 {{notes}}',
  coam_search_ph: '搜索科目码、名称、Subgroup 或 Account Type',
  coam_filter_category: '大类',
  coam_filter_type: '科目类型',
  coam_all_categories: '全部大类',
  coam_all_types: '全部科目类型',
  coam_clear: '清除',
  coam_export: '下载模板 (.xlsx)',
  coam_export_hint: '整份模板——全部 {{n}} 个科目和 README,按客户提供的原样——与当前搜索无关。',
  coam_col_code: '科目码',
  coam_col_th: '泰文名称',
  coam_col_en: '英文名称',
  coam_col_type: '科目类型',
  coam_col_subgroup: '子分组',
  coam_col_side: '正常余额方向',
  coam_col_notes: '系统 / 备注',
  coam_expand_all: '全部展开',
  coam_collapse_all: '全部折叠',
  coam_expand: '展开 {{code}}',
  coam_collapse: '折叠 {{code}}',
  coam_showing_all: '共 {{n}} 个科目,按 Parent Account 排成树。展开一行即可看到其下的科目。',
  coam_showing_match: '{{n}} 个科目中有 {{matched}} 个符合。其上级科目以灰色显示,便于沿树查看。',
  coam_no_match: '没有符合的科目。',
  coam_context_tip: '因其下有符合条件的科目而显示',
  coam_opposite_tip: '正常余额 {{side}};其顶层科目 {{top}} 为 {{topSide}}。',
  coam_legend: '标记的类型',
  coam_kind_header: '归集用科目,不直接记账',
  coam_kind_control: '通常由系统控制(应收、应付、增值税等)',
  coam_kind_contra: '用来抵减与其配对的科目,因此记相反方向',
  coam_kind_system: '由系统生成',
  coam_kind_reference: '参考数据,不记账',
  coam_obs_title: '原件观察',
  coam_obs_lead: '客户原件如此。以下五条描述的是原件本身的样子——都不是错误,本页面也不改动其中任何一条。',
  coam_obs1_title: 'Header 科目',
  coam_obs1_all: '{{total}} 个 Header 科目全部有子科目(没有空表头)。',
  coam_obs1_some: '{{total}} 个 Header 科目中有 {{with}} 个有子科目。以下没有子科目:',
  coam_obs2_title: '有子科目的非 Header 科目',
  coam_obs2_text: '不是 Header 但也有子科目的科目:{{n}} 个',
  coam_obs2_none: '凡有子科目的科目都是 Header 科目。',
  coam_obs2_note: '多半是「控制科目 / 资产科目下挂明细、累计折旧或累计摊销」,是客户的设计;是否该把其中某个当作 Header 是一项判断,记在事务所判断层里——从不写进客户原件。',
  coam_export_hint_firm: '客户原件,按客户提供的原样——全部 {{n}} 个科目和 README——不含事务所判断(判断另存)。',
  coam_col_firm: '事务所判断',
  coam_obs2_judged: '这 {{total}} 个科目中有 {{judged}} 个的类型已有事务所判断。',
  coam_view: '显示',
  coam_view_customer: '客户原件',
  coam_view_firm: '套用事务所判断',
  coam_firm_title: '事务所判断',
  coam_firm_ok_one: '生效中——{{count}} 条判断',
  coam_firm_ok_other: '生效中——{{count}} 条判断',
  coam_firm_blocked: '需人工查看,暂不可用',
  coam_firm_lead: '事务所对客户原件作出的判断——哪些科目是分组表头、哪个明细科目承担应收 / 应付控制——每条都带理由、判断人和时间。另存在原件旁边,从不写进原件:客户的每一格和下载文件都保持原样。',
  coam_firm_blocked_lead: '判断层照常显示,但只要以下任一情况存在,任何复制或发布都不得使用它:',
  coam_firm_counts: '科目数',
  coam_firm_count_header: '分组表头(不过账)',
  coam_firm_count_postable: '可过账',
  coam_firm_count_reference: '参考资料(不过账)',
  coam_firm_count_system: '系统生成(不过账)',
  coam_firm_count_control: '类型为控制科目',
  coam_firm_count_ar: '应收控制科目',
  coam_firm_count_ap: '应付控制科目',
  coam_firm_none: '(无)',
  coam_firm_role_ar: '应收控制',
  coam_firm_role_ap: '应付控制',
  coam_firm_field_account_type: '科目类型',
  coam_firm_field_control_role: '控制角色',
  coam_firm_field_name_th: '泰文名称',
  coam_firm_field_name_en: '英文名称',
  coam_firm_field_subgroup: '分组(Subgroup)',
  coam_firm_field_system_notes: '系统 / 备注',
  coam_firm_kept: '已审阅——保留客户原值:',
  coam_firm_stale: '未生效——客户原件这一格已变',
  coam_firm_by: '判断人 {{who}}',
  coam_firm_recorded: '录入人 {{who}}({{src}})',
  coam_firm_rule: '事务所修改…',
  coam_firm_rule_tip: '记录事务所对 {{code}} 的判断',
  coam_firm_dialog_title: '事务所对 {{code}} 的判断',
  coam_firm_field: '字段',
  coam_firm_customer_value: '客户原值',
  coam_firm_in_force: '当前生效:',
  coam_firm_action: '判断',
  coam_firm_act_set: '改用另一个值',
  coam_firm_act_confirm: '已审阅——保留客户原值',
  coam_firm_act_withdraw: '撤回判断——恢复客户原值',
  coam_firm_value: '事务所的值',
  coam_firm_words_hint: '只能选本模板自己的科目类型。',
  coam_firm_reason: '理由',
  coam_firm_decision_ref: '决定出处(可不填)',
  coam_firm_reason_required: '理由必填:用一句复核人能核对的话说明为什么。',
  coam_firm_history: '这一格的判断记录(新的在前)',
  coam_firm_cancel: '取消',
  coam_firm_submit: '记录判断',
  coam_firm_discard: '不记录这条判断就关闭?已输入的内容会丢失。',
  coam_sub_count_one: '{{count}} 个子科目',
  coam_sub_count_other: '{{count}} 个子科目',
  coam_obs3_title: '正常余额与大类方向相反',
  coam_obs3_text: '正常余额与其顶层科目方向相反的科目:{{n}} 个',
  coam_obs3_none: '每个科目的正常余额都与其顶层科目方向相同。',
  coam_obs3_all_contra: '全部是 Contra 类型。Contra 科目用来抵减与其配对的科目,本来就记相反方向——这是对的,不是错误。',
  coam_obs3_some_contra: '其中 Contra 类型按设计记相反方向;其余按原件列出。',
  coam_obs4_title: '重名科目',
  coam_obs4_text: '英文名相同:{{en}} 组 · 泰文名相同:{{th}} 组。原件就是这样命名的,科目码可以区分它们。',
  coam_obs4_none: '没有重名的科目。',
  coam_obs4_header_child: '表头 + 同名子科目',
  coam_obs4_separate: '两个不同的科目',
  coam_obs4_same_both: '英文名和泰文名都相同',
  coam_obs4_same_en: '英文名相同',
  coam_obs4_same_th: '泰文名相同',
  coam_obs5_title: '特殊类型',
  coam_obs5_text: '原件中单独标出的科目类型:',
  coam_obs5_none: '原件中没有 Control、System 或 Reference 类型。',
  coam_readme_said: '客户 README:',
  coam_readme_title: '客户 README 原话',
  coam_readme_lead: '原样引用,未改写。',
};
export const th: Record<string, string> = {
  coam_tab: 'แม่แบบผังบัญชี',
  coam_title: 'แม่แบบผังบัญชีหลัก',
  coam_readonly: 'ไฟล์ของลูกค้าเป็นแบบอ่านอย่างเดียว แสดงตามที่ลูกค้าส่งมาทุกประการ และจัดเก็บแยกจากผังบัญชีของลูกค้าทุกราย ความเห็นของสำนักงานต่อไฟล์นี้เก็บไว้แยกต่างหาก ไม่เคยเขียนลงในไฟล์ของลูกค้า',
  coam_template: 'แม่แบบ',
  coam_loading: 'กำลังโหลด…',
  coam_none: 'ยังไม่มีแม่แบบผังบัญชีหลักที่โหลดไว้',
  coam_last_record: 'บันทึกการโหลดล่าสุด:',
  coam_error: 'โหลดแม่แบบไม่สำเร็จ: {{error}}',
  coam_src_file: 'ไฟล์',
  coam_src_sheet: 'ชีต',
  coam_src_sha: 'SHA-256',
  coam_src_imported: 'นำเข้าเมื่อ',
  coam_src_by: 'โดย {{who}}',
  coam_src_by_system: 'มาพร้อมกับระบบ จากไฟล์ต้นฉบับของลูกค้า',
  coam_src_rows: 'จำนวนแถว',
  coam_count_accounts: 'บัญชี',
  coam_count_categories: 'หมวดหลัก',
  coam_count_subgroups: 'กลุ่มย่อย',
  coam_count_types: 'ประเภทบัญชี',
  coam_count_depth: 'ความลึกของโครงสร้าง',
  coam_depth_hint: 'บัญชีที่อยู่ลึกที่สุดอยู่ต่ำกว่าบัญชีระดับบนสุด {{n}} ขั้น (นับตาม Parent Account)',
  coam_count_more: 'ระดับบนสุด {{top}} · Dr {{dr}} / Cr {{cr}} · มีหมายเหตุ {{notes}}',
  coam_search_ph: 'ค้นหารหัส ชื่อบัญชี Subgroup หรือ Account Type',
  coam_filter_category: 'หมวดหลัก',
  coam_filter_type: 'ประเภทบัญชี',
  coam_all_categories: 'ทุกหมวดหลัก',
  coam_all_types: 'ทุกประเภทบัญชี',
  coam_clear: 'ล้าง',
  coam_export: 'ดาวน์โหลดแม่แบบ (.xlsx)',
  coam_export_hint: 'แม่แบบทั้งฉบับ — บัญชีทั้ง {{n}} บัญชีและ README ตามที่ลูกค้าส่งมา — ไม่ขึ้นกับผลการค้นหา',
  coam_col_code: 'รหัสบัญชี',
  coam_col_th: 'ชื่อภาษาไทย',
  coam_col_en: 'ชื่อภาษาอังกฤษ',
  coam_col_type: 'ประเภทบัญชี',
  coam_col_subgroup: 'กลุ่มย่อย',
  coam_col_side: 'ยอดคงเหลือปกติ',
  coam_col_notes: 'ระบบ / หมายเหตุ',
  coam_expand_all: 'ขยายทั้งหมด',
  coam_collapse_all: 'ยุบทั้งหมด',
  coam_expand: 'ขยาย {{code}}',
  coam_collapse: 'ยุบ {{code}}',
  coam_showing_all: 'บัญชีทั้งหมด {{n}} บัญชี จัดเรียงตาม Parent Account เปิดแถวเพื่อดูบัญชีที่อยู่ภายใต้',
  coam_showing_match: 'ตรงกับการค้นหา {{matched}} จาก {{n}} บัญชี บัญชีระดับบนของบัญชีเหล่านั้นแสดงเป็นสีเทาเพื่อให้ไล่ตามโครงสร้างได้',
  coam_no_match: 'ไม่มีบัญชีที่ตรงกับการค้นหา',
  coam_context_tip: 'แสดงไว้เพราะมีบัญชีที่อยู่ภายใต้ตรงกับการค้นหา',
  coam_opposite_tip: 'ยอดคงเหลือปกติ {{side}} ส่วนบัญชีระดับบนสุด {{top}} เป็น {{topSide}}',
  coam_legend: 'ประเภทที่มีเครื่องหมาย',
  coam_kind_header: 'บัญชีสำหรับจัดกลุ่ม ไม่ใช้บันทึกรายการ',
  coam_kind_control: 'โดยปกติควบคุมโดยระบบ (ลูกหนี้ เจ้าหนี้ VAT และบัญชีทำนองเดียวกัน)',
  coam_kind_contra: 'ใช้หักล้างบัญชีที่จับคู่กัน จึงมียอดอยู่ด้านตรงข้าม',
  coam_kind_system: 'ระบบสร้างขึ้น',
  coam_kind_reference: 'ข้อมูลอ้างอิง ไม่ใช้บันทึกรายการ',
  coam_obs_title: 'ข้อสังเกตเกี่ยวกับไฟล์ของลูกค้า',
  coam_obs_lead: 'ไฟล์ต้นฉบับของลูกค้าเป็นเช่นนี้ ห้าข้อด้านล่างอธิบายลักษณะของต้นฉบับตามที่ส่งมา — ไม่มีข้อใดเป็นข้อผิดพลาด และหน้านี้ไม่ได้เปลี่ยนแปลงข้อใด',
  coam_obs1_title: 'บัญชี Header',
  coam_obs1_all: 'บัญชี Header ทั้ง {{total}} บัญชีมีบัญชีย่อยทุกบัญชี — ไม่มี Header ที่ว่างเปล่า',
  coam_obs1_some: 'บัญชี Header {{with}} จาก {{total}} บัญชีมีบัญชีย่อย บัญชีต่อไปนี้ไม่มี:',
  coam_obs2_title: 'บัญชีอื่นที่มีบัญชีย่อย',
  coam_obs2_text: 'บัญชีที่ไม่ใช่ Header แต่มีบัญชีย่อย: {{n}} บัญชี',
  coam_obs2_none: 'ทุกบัญชีที่มีบัญชีย่อยเป็นบัญชี Header',
  coam_obs2_note: 'ส่วนใหญ่น่าจะเป็นบัญชีคุมยอดหรือบัญชีสินทรัพย์ที่มีบัญชีรายละเอียด ค่าเสื่อมราคาสะสม หรือค่าตัดจำหน่ายสะสมอยู่ภายใต้ นี่คือการออกแบบของลูกค้า ส่วนจะถือบัญชีใดเป็น Header หรือไม่เป็นดุลยพินิจที่บันทึกไว้ในชั้นความเห็นของสำนักงาน — ไม่เคยเขียนลงในไฟล์ของลูกค้า',
  coam_export_hint_firm: 'ไฟล์ของลูกค้าตามที่ส่งมาทุกประการ — บัญชีทั้ง {{n}} บัญชีและ README — ไม่รวมความเห็นของสำนักงาน ซึ่งเก็บไว้แยกกัน',
  coam_col_firm: 'ความเห็นของสำนักงาน',
  coam_obs2_judged: 'บัญชี {{judged}} จาก {{total}} บัญชีนี้มีความเห็นของสำนักงานเรื่องประเภทบัญชีแล้ว',
  coam_view: 'แสดง',
  coam_view_customer: 'ไฟล์ของลูกค้า',
  coam_view_firm: 'รวมความเห็นของสำนักงาน',
  coam_firm_title: 'ความเห็นของสำนักงาน',
  coam_firm_ok_one: 'มีผลใช้ — {{count}} รายการ',
  coam_firm_ok_other: 'มีผลใช้ — {{count}} รายการ',
  coam_firm_blocked: 'ยังใช้ไม่ได้จนกว่าจะมีผู้ตรวจดู',
  coam_firm_lead: 'ความเห็นที่สำนักงานให้ไว้ต่อไฟล์ของลูกค้า — บัญชีใดเป็นหัวข้อจัดกลุ่ม บัญชีย่อยใดเป็นบัญชีคุมลูกหนี้ / เจ้าหนี้ — แต่ละรายการมีเหตุผล ผู้ให้ความเห็น และเวลา เก็บไว้ข้างไฟล์ของลูกค้า ไม่เคยเขียนลงไป: ทุกช่องของลูกค้าและไฟล์ที่ดาวน์โหลดคงเดิมทุกประการ',
  coam_firm_blocked_lead: 'ชั้นความเห็นยังแสดงอยู่ แต่ห้ามคัดลอกหรือเผยแพร่ตราบที่ยังมีข้อใดต่อไปนี้:',
  coam_firm_counts: 'จำนวนบัญชี',
  coam_firm_count_header: 'หัวข้อจัดกลุ่ม (ไม่ลงรายการ)',
  coam_firm_count_postable: 'ลงรายการได้',
  coam_firm_count_reference: 'ข้อมูลอ้างอิง (ไม่ลงรายการ)',
  coam_firm_count_system: 'ระบบสร้างขึ้น (ไม่ลงรายการ)',
  coam_firm_count_control: 'ประเภทเป็นบัญชีคุมยอด',
  coam_firm_count_ar: 'บัญชีคุมลูกหนี้',
  coam_firm_count_ap: 'บัญชีคุมเจ้าหนี้',
  coam_firm_none: '(ไม่มี)',
  coam_firm_role_ar: 'คุมลูกหนี้',
  coam_firm_role_ap: 'คุมเจ้าหนี้',
  coam_firm_field_account_type: 'ประเภทบัญชี',
  coam_firm_field_control_role: 'บทบาทบัญชีคุม',
  coam_firm_field_name_th: 'ชื่อภาษาไทย',
  coam_firm_field_name_en: 'ชื่อภาษาอังกฤษ',
  coam_firm_field_subgroup: 'กลุ่มย่อย (Subgroup)',
  coam_firm_field_system_notes: 'ระบบ / หมายเหตุ',
  coam_firm_kept: 'ตรวจแล้ว — คงค่าของลูกค้าไว้:',
  coam_firm_stale: 'ไม่มีผล — ช่องนี้ในไฟล์ของลูกค้าเปลี่ยนไปแล้ว',
  coam_firm_by: 'ความเห็นโดย {{who}}',
  coam_firm_recorded: 'บันทึกโดย {{who}} ({{src}})',
  coam_firm_rule: 'ความเห็นของสำนักงาน…',
  coam_firm_rule_tip: 'บันทึกความเห็นของสำนักงานต่อ {{code}}',
  coam_firm_dialog_title: 'ความเห็นของสำนักงานต่อ {{code}}',
  coam_firm_field: 'ช่องข้อมูล',
  coam_firm_customer_value: 'ค่าของลูกค้า',
  coam_firm_in_force: 'ที่มีผลอยู่ตอนนี้:',
  coam_firm_action: 'ความเห็น',
  coam_firm_act_set: 'ใช้ค่าอื่น',
  coam_firm_act_confirm: 'ตรวจแล้ว — คงค่าของลูกค้า',
  coam_firm_act_withdraw: 'ถอนความเห็น — กลับไปใช้ค่าของลูกค้า',
  coam_firm_value: 'ค่าของสำนักงาน',
  coam_firm_words_hint: 'เลือกได้เฉพาะประเภทบัญชีที่มีในแม่แบบนี้เท่านั้น',
  coam_firm_reason: 'เหตุผล',
  coam_firm_decision_ref: 'อ้างอิงคำตัดสิน (ไม่บังคับ)',
  coam_firm_reason_required: 'ต้องระบุเหตุผล: บอกว่าทำไม เป็นประโยคที่ผู้ตรวจทานตรวจสอบได้',
  coam_firm_history: 'ความเห็นของช่องนี้ ล่าสุดก่อน',
  coam_firm_cancel: 'ยกเลิก',
  coam_firm_submit: 'บันทึกความเห็น',
  coam_firm_discard: 'ปิดโดยไม่บันทึกความเห็นนี้หรือไม่? ข้อความที่พิมพ์ไว้จะหายไป',
  coam_sub_count_one: 'บัญชีย่อย {{count}} บัญชี',
  coam_sub_count_other: 'บัญชีย่อย {{count}} บัญชี',
  coam_obs3_title: 'ยอดคงเหลือปกติอยู่ด้านตรงข้ามกับหมวด',
  coam_obs3_text: 'บัญชีที่ยอดคงเหลือปกติอยู่ด้านตรงข้ามกับบัญชีระดับบนสุดของตน: {{n}} บัญชี',
  coam_obs3_none: 'ทุกบัญชีมียอดคงเหลือปกติด้านเดียวกับบัญชีระดับบนสุดของตน',
  coam_obs3_all_contra: 'ทั้งหมดเป็นประเภท Contra บัญชี Contra ใช้หักล้างบัญชีที่จับคู่กัน จึงต้องอยู่ด้านตรงข้าม — ถูกต้องแล้ว ไม่ใช่ข้อผิดพลาด',
  coam_obs3_some_contra: 'บัญชีประเภท Contra ในกลุ่มนี้อยู่ด้านตรงข้ามโดยการออกแบบ บัญชีอื่นแสดงตามที่ไฟล์ระบุ',
  coam_obs4_title: 'บัญชีที่มีชื่อซ้ำกัน',
  coam_obs4_text: 'ชื่อภาษาอังกฤษซ้ำ: {{en}} กลุ่ม · ชื่อภาษาไทยซ้ำ: {{th}} กลุ่ม ไฟล์ตั้งชื่อไว้เช่นนี้ และรหัสบัญชีแยกบัญชีเหล่านี้ออกจากกัน',
  coam_obs4_none: 'ไม่มีบัญชีใดที่ชื่อซ้ำกัน',
  coam_obs4_header_child: 'Header กับบัญชีย่อยของตนเอง',
  coam_obs4_separate: 'สองบัญชีที่แยกกัน',
  coam_obs4_same_both: 'ชื่อภาษาอังกฤษและภาษาไทยซ้ำกัน',
  coam_obs4_same_en: 'ชื่อภาษาอังกฤษซ้ำกัน',
  coam_obs4_same_th: 'ชื่อภาษาไทยซ้ำกัน',
  coam_obs5_title: 'ประเภทบัญชีพิเศษ',
  coam_obs5_text: 'ประเภทบัญชีที่ไฟล์แยกไว้เป็นพิเศษ:',
  coam_obs5_none: 'ไฟล์ไม่มีประเภทบัญชี Control, System หรือ Reference',
  coam_readme_said: 'README ของลูกค้า:',
  coam_readme_title: 'README ของลูกค้า',
  coam_readme_lead: 'ยกมาตามต้นฉบับ ไม่ได้เรียบเรียงใหม่',
};
